import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import { diretoriaToneTextClass } from '@/lib/diretoriaKpiTone';
import { DiretoriaSparkline } from '@/components/Diretoria/DiretoriaSparkline';
import {
  DiretoriaSqlButton,
  type DiretoriaSqlButtonProps,
} from '@/components/Diretoria/DiretoriaSqlButton';
import { DiretoriaIndicatorCardSkeleton, DiretoriaBlockSkeleton } from '@/components/Diretoria/DiretoriaIndicatorCardSkeleton';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import type { DiretoriaInadimplenciaPayload } from '@/lib/diretoriaErpIndicatorBuilders';
import { formatCurrencyCompact } from '@/lib/formatters';
import { DIRETORIA_SHOW_MULTAS } from '@/data/diretoriaConstants';
const COLOR_A = 'hsl(222 47% 18%)';
const COLOR_B = '#ca8a04';
const COLOR_C = '#0f766e';
const PRODUCT_COLORS = [COLOR_A, COLOR_B, COLOR_C, '#7c3aed', '#b45309', '#0369a1'] as const;

function pctOf(part: number, total: number): string {
  if (!total || !Number.isFinite(total)) return '—';
  return `${((part / total) * 100).toFixed(1).replace('.', ',')}%`;
}

function truncateLabel(raw: unknown, max = 16): string {
  const s = String(raw ?? '').trim() || '—';
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Rótulo curto da empresa no eixo X (ex.: ZEH MOTOCA NATAL → NATAL). */
function shortEmpresaLabel(nomeEmpresa: unknown): string {
  const raw = String(nomeEmpresa ?? '').trim() || 'Sem empresa';
  return truncateLabel(raw.replace(/^ZEH\s+MOTOCA\s+/i, ''), 18);
}

/** Tooltip Recharts que não quebra se `payload` vier undefined (crash reading '0'). */
function SafeChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number | string;
    color?: string;
    payload?: { fullName?: string };
  }>;
  label?: string | number;
}) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const title =
    String(payload[0]?.payload?.fullName ?? '').trim() ||
    (label != null && label !== '' ? String(label) : '');
  return (
    <div className="rounded-md border border-border bg-background px-2.5 py-1.5 text-xs shadow-md">
      {title ? <p className="mb-0.5 font-medium text-foreground">{title}</p> : null}
      {payload.map((entry, i) => (
        <p key={`${entry?.name ?? i}`} className="tabular-nums text-muted-foreground">
          <span style={{ color: entry?.color }}>{entry?.name ?? 'Série'}</span>:{' '}
          {entry?.value == null || entry.value === ''
            ? '—'
            : typeof entry.value === 'number'
              ? entry.value.toLocaleString('pt-BR')
              : String(entry.value)}
        </p>
      ))}
    </div>
  );
}

class ChartBlockBoundary extends React.Component<
  { children: React.ReactNode; fallback?: string; resetKey?: string | number },
  { error?: Error }
> {
  state: { error?: Error } = {};
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error('Diretoria Faturamento chart crash:', error);
  }
  componentDidUpdate(prevProps: Readonly<{ resetKey?: string | number }>) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: undefined });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <p className="flex h-full min-h-[8rem] items-center justify-center px-3 text-center text-sm text-muted-foreground">
          {this.props.fallback ?? 'Gráfico indisponível com os dados atuais'}
        </p>
      );
    }
    return this.props.children;
  }
}

export interface FaturamentoProdutoPayload {
  itens: Array<{
    name: string;
    produto: string;
    nomeEmpresa: string;
    totalPago: number;
    qtdLocacoes?: number;
  }>;
  porProduto?: Array<{
    produto: string;
    totalPago: number;
    qtdLocacoes: number;
    ticketMedio: number;
  }>;
  produtos: string[];
  dimensaoProduto?: string;
  querySql?: string;
}

export interface InfracoesExtPayload {
  mensal?: Array<{ name: string; qtdPagas: number; valorPagas: number }>;
  porEmpresa?: Array<{
    nomeEmpresa: string;
    qtdPagas: number;
    qtdVencidas: number;
    qtdAVencer: number;
    valorPagas: number;
    valorVencidas?: number;
    valorAVencer?: number;
    valorAberto: number;
  }>;
  querySql?: string;
}

export interface DiretoriaFaturamentoSectionProps {
  indicators: DiretoriaIndicator[];
  /** id → ainda aguardando PortalFly/ERP */
  loadingById?: Record<string, boolean>;
  onOpenIndicator: (ind: DiretoriaIndicator) => void;
  resolveIndicatorSql: (ind: DiretoriaIndicator) => DiretoriaSqlButtonProps;
  formatCurrency: (n: number) => string;
  companyId?: string;
  inadimplencia?: DiretoriaInadimplenciaPayload | null;
  faturamentoProduto?: FaturamentoProdutoPayload | null;
  infracoesExt?: InfracoesExtPayload | null;
}

export const DiretoriaFaturamentoSection: React.FC<DiretoriaFaturamentoSectionProps> = ({
  indicators,
  loadingById,
  onOpenIndicator,
  resolveIndicatorSql,
  formatCurrency,
  companyId,
  inadimplencia,
  faturamentoProduto,
  infracoesExt,
}) => {
  const titulosHref = useMemo(() => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    const q = params.toString();
    return q ? `/titulos?${q}` : '/titulos';
  }, [companyId]);

  const kanbanHref = useMemo(() => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    const q = params.toString();
    return q ? `/kanban?${q}` : '/kanban';
  }, [companyId]);

  const porEmpresa = useMemo(() => {
    return (inadimplencia?.porEmpresa ?? [])
      .filter((row) => row && typeof row === 'object')
      .slice()
      .sort((a, b) => Number(b.valor ?? 0) - Number(a.valor ?? 0))
      .slice(0, 8);
  }, [inadimplencia]);

  const saldo = Number(inadimplencia?.saldoVencido ?? 0) || 0;
  const titulos = Number(inadimplencia?.titulosVencidos ?? 0) || 0;
  const faixas = (inadimplencia?.faixas ?? []).filter((f) => f && f.faixa != null);
  const totalFaixa = faixas.reduce((s, f) => s + Number(f.saldo ?? 0), 0) || saldo;

  const produtoStacked = useMemo(() => {
    const itens = (faturamentoProduto?.itens ?? []).filter(
      (item) => item && String(item.produto ?? '').trim()
    );

    // Totais por produto (ordem do gráfico / legenda).
    const produtoTotals = new Map<string, number>();
    const empresaTotals = new Map<string, number>();
    for (const item of itens) {
      const produto = String(item.produto).trim() || 'Sem produto';
      const empresa = shortEmpresaLabel(item.nomeEmpresa);
      const valor = Number(item.totalPago) || 0;
      produtoTotals.set(produto, (produtoTotals.get(produto) ?? 0) + valor);
      empresaTotals.set(empresa, (empresaTotals.get(empresa) ?? 0) + valor);
    }

    const stackKeys = [...produtoTotals.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'pt-BR'))
      .map(([produto]) => produto);

    // Uma barra por empresa; pilha = todos os produtos (período agregado).
    const byEmpresa = new Map<string, Record<string, number | string>>();
    for (const item of itens) {
      const produto = String(item.produto).trim() || 'Sem produto';
      if (!stackKeys.includes(produto)) continue;
      const empresa = shortEmpresaLabel(item.nomeEmpresa);
      const row = byEmpresa.get(empresa) ?? { name: empresa };
      row[produto] = (Number(row[produto] ?? 0) || 0) + (Number(item.totalPago) || 0);
      byEmpresa.set(empresa, row);
    }

    const data = [...byEmpresa.entries()]
      .sort((a, b) => (empresaTotals.get(b[0]) ?? 0) - (empresaTotals.get(a[0]) ?? 0))
      .map(([, row]) => row);

    return { data, keys: stackKeys };
  }, [faturamentoProduto]);

  const produtoTabela = useMemo(() => {
    const porProduto = faturamentoProduto?.porProduto ?? [];

    if (porProduto.length > 0) {
      return [...porProduto]
        .map((p) => ({
          produto: String(p.produto ?? '').trim() || 'Sem produto',
          totalPago: Number(p.totalPago) || 0,
          qtdLocacoes: Number(p.qtdLocacoes) || 0,
          ticketMedio: Number(p.ticketMedio) || 0,
        }))
        .sort((a, b) => b.totalPago - a.totalPago || a.produto.localeCompare(b.produto, 'pt-BR'));
    }

    // Fallback: agregar só valor a partir dos itens (sem qtd/ticket se API antiga)
    const map = new Map<string, number>();
    for (const item of faturamentoProduto?.itens ?? []) {
      const produto = String(item.produto ?? '').trim() || 'Sem produto';
      map.set(produto, (map.get(produto) ?? 0) + (Number(item.totalPago) || 0));
    }
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'pt-BR'))
      .map(([produto, totalPago]) => ({
        produto,
        totalPago,
        qtdLocacoes: 0,
        ticketMedio: 0,
      }));
  }, [faturamentoProduto]);

  const mensalMultas = useMemo(
    () =>
      (infracoesExt?.mensal ?? [])
        .filter((m) => m && String(m.name ?? '').trim())
        .map((m) => ({
          name: String(m.name),
          qtdPagas: Number(m.qtdPagas ?? 0) || 0,
          valorPagas: Number(m.valorPagas ?? 0) || 0,
        })),
    [infracoesExt]
  );

  const multasPorEmpresa = useMemo(() => {
    return (infracoesExt?.porEmpresa ?? [])
      .filter((e) => e && String(e.nomeEmpresa ?? '').trim())
      .map((e) => {
        const pagas = Number(e.qtdPagas ?? 0) || 0;
        const aVencer = Number(e.qtdAVencer ?? 0) || 0;
        const vencidas = Number(e.qtdVencidas ?? 0) || 0;
        return {
          name: shortEmpresaLabel(e.nomeEmpresa),
          fullName: String(e.nomeEmpresa ?? '').trim() || '—',
          pagas,
          aVencer,
          vencidas,
          total: pagas + aVencer + vencidas,
        };
      })
      .filter((e) => e.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [infracoesExt]);

  const safeIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  const loadingInad = Boolean(loadingById?.inadimplencia);
  const loadingProduto = Boolean(loadingById?.['faturamento-produto']);
  const loadingInfracoes = Boolean(loadingById?.infracoes);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground">Faturamento</h2>
          <p className="text-xs text-muted-foreground">
            {DIRETORIA_SHOW_MULTAS
              ? 'Inadimplência, faturamento por produto × empresa e multas · fonte: PortalFly (títulos)'
              : 'Inadimplência e faturamento por produto × empresa · fonte: PortalFly (títulos)'}
          </p>
        </div>
      </div>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <CardTitle className="text-base">Inadimplência</CardTitle>
              <DiretoriaSqlButton
                sql={inadimplencia?.querySql}
                source="cobranca"
                title="Inadimplência — consulta SQL"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={titulosHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Títulos
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
              <Link
                to={kanbanHref}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Kanban
                <ExternalLink className="h-3 w-3" aria-hidden />
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2">
          {loadingInad ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <DiretoriaBlockSkeleton key={i} className="h-16" label="Carregando KPI" />
                ))}
              </div>
              <DiretoriaBlockSkeleton className="h-48" label="Carregando inadimplência" />
            </>
          ) : (
            <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div>
              <p className="text-[11px] uppercase text-muted-foreground">Saldo vencido</p>
              <p className={cn('font-heading text-xl font-bold tabular-nums', diretoriaToneTextClass('bad'))}>
                {formatCurrency(saldo)}
              </p>
              <p className="text-xs text-muted-foreground">{pctOf(saldo, totalFaixa || saldo)} do aging</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-muted-foreground">Títulos</p>
              <p className="font-heading text-xl font-bold tabular-nums">{titulos}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-muted-foreground">Pessoas</p>
              <p className="font-heading text-xl font-bold tabular-nums">
                {inadimplencia?.pessoasDistintas ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">distintas</p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-muted-foreground">Méd. títulos/pessoa</p>
              <p className="font-heading text-xl font-bold tabular-nums">
                {inadimplencia?.mediaTitulosPorPessoa != null
                  ? inadimplencia.mediaTitulosPorPessoa.toFixed(1).replace('.', ',')
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase text-muted-foreground">Novos no mês</p>
              <p className="font-heading text-xl font-bold tabular-nums">{inadimplencia?.novosMes ?? 0}</p>
            </div>
          </div>
          <div className="h-48">
            <ChartBlockBoundary>
              {faixas.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={faixas.map((f) => ({
                      name: String(f.faixa),
                      qtd: Number(f.qtd ?? 0) || 0,
                      saldo: Math.round(Number(f.saldo ?? 0) || 0),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Bar dataKey="qtd" name="Qtd." fill={COLOR_A} isAnimationActive={false} />
                    <Bar dataKey="saldo" name="Saldo" fill={COLOR_B} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem dados de inadimplência
                </p>
              )}
            </ChartBlockBoundary>
          </div>
          {porEmpresa.length > 0 ? (
            <div className="lg:col-span-2">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Por empresa (PortalFly)
              </p>
              <div className="overflow-x-auto rounded-md border border-border/80">
                <table className="w-full min-w-[320px] text-left text-sm">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Empresa</th>
                      <th className="px-3 py-2 text-right font-medium">Pessoas</th>
                      <th className="px-3 py-2 text-right font-medium">Títulos</th>
                      <th className="px-3 py-2 text-right font-medium">Méd./pessoa</th>
                      <th className="px-3 py-2 text-right font-medium">Saldo</th>
                      <th className="px-3 py-2 text-right font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porEmpresa.map((row, idx) => (
                      <tr key={`${row.empresa ?? idx}`} className="border-t border-border/60">
                        <td className="px-3 py-1.5">{row.empresa || '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.pessoas ?? 0}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.count ?? 0}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {row.mediaTitulosPorPessoa != null
                            ? row.mediaTitulosPorPessoa.toFixed(1).replace('.', ',')
                            : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {formatCurrency(Number(row.valor ?? 0))}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                          {pctOf(Number(row.valor ?? 0), saldo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <div className={DIRETORIA_SHOW_MULTAS ? 'grid grid-cols-1 gap-4 xl:grid-cols-2' : 'grid grid-cols-1 gap-4'}>
        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Faturamento por produto × empresa</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Uma barra por empresa · todos os produtos · ticket = faturado / locações
                  distintas no período
                </p>
              </div>
              <DiretoriaSqlButton
                sql={faturamentoProduto?.querySql}
                source="cobranca"
                title="Faturamento por produto × empresa — consulta SQL"
              />
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {loadingProduto ? (
              <DiretoriaBlockSkeleton className="h-72" label="Carregando faturamento por produto" />
            ) : (
            <>
            <div className="h-72">
            <ChartBlockBoundary>
              {produtoStacked.data.length > 0 && produtoStacked.keys.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={produtoStacked.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {produtoStacked.keys.map((key, i) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={PRODUCT_COLORS[i % PRODUCT_COLORS.length]}
                        isAnimationActive={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem faturamento por produto × empresa no período
                </p>
              )}
            </ChartBlockBoundary>
            </div>
            {produtoTabela.length > 0 ? (
              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-1.5 font-medium">Produto</th>
                      <th className="px-2 py-1.5 font-medium text-right">Locações</th>
                      <th className="px-2 py-1.5 font-medium text-right">Faturado</th>
                      <th className="px-2 py-1.5 font-medium text-right">Ticket médio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {produtoTabela.map((row) => (
                      <tr
                        key={row.produto}
                        className="border-t border-border/60"
                      >
                        <td className="px-2 py-1.5 max-w-[14rem] truncate" title={row.produto}>
                          {row.produto}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {row.qtdLocacoes > 0
                            ? row.qtdLocacoes.toLocaleString('pt-BR')
                            : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {formatCurrency(row.totalPago)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {row.qtdLocacoes > 0 && row.ticketMedio > 0
                            ? formatCurrency(row.ticketMedio)
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            </>
            )}
          </CardContent>
        </Card>

        {DIRETORIA_SHOW_MULTAS ? (
          <>
        <Card className="border-border/80">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Multas pagas (mês a mês)</CardTitle>
                <p className="text-xs text-muted-foreground">
                  12 meses do ano de referência · multas PortalFly (cobrança a receber) · is_multa/texto Multa
                </p>
              </div>
              <DiretoriaSqlButton
                sql={infracoesExt?.querySql}
                source="cobranca"
                title="Multas pagas — consulta SQL"
              />
            </div>
          </CardHeader>
          <CardContent className="h-64 pt-0">
            {loadingInfracoes ? (
              <DiretoriaBlockSkeleton className="h-full" label="Carregando multas mensais" />
            ) : (
            <ChartBlockBoundary>
              {mensalMultas.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={mensalMultas} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="qtd"
                      orientation="left"
                      allowDecimals={false}
                      tick={{ fontSize: 10 }}
                      width={36}
                      stroke={COLOR_A}
                    />
                    <YAxis
                      yAxisId="valor"
                      orientation="right"
                      tick={{ fontSize: 10 }}
                      width={52}
                      stroke={COLOR_B}
                      tickFormatter={(v) => formatCurrencyCompact(Number(v))}
                    />
                    <Tooltip content={SafeChartTooltip} />
                    <Legend />
                    <Line
                      yAxisId="qtd"
                      type="monotone"
                      dataKey="qtdPagas"
                      name="Qtd. pagas"
                      stroke={COLOR_A}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    <Line
                      yAxisId="valor"
                      type="monotone"
                      dataKey="valorPagas"
                      name="Valor"
                      stroke={COLOR_B}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem série de multas no período
                </p>
              )}
            </ChartBlockBoundary>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/80 xl:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">Multas por empresa</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  PortalFly · cobrança a receber (tipo=RECEBER na base) · is_multa/texto Multa
                </p>
              </div>
              <DiretoriaSqlButton
                sql={infracoesExt?.querySql}
                source="cobranca"
                title="Multas por empresa — consulta SQL"
              />
            </div>
          </CardHeader>
          <CardContent className="h-72 pt-0">
            {loadingInfracoes ? (
              <DiretoriaBlockSkeleton className="h-full" label="Carregando multas por empresa" />
            ) : (
            <ChartBlockBoundary resetKey={multasPorEmpresa.length}>
              {multasPorEmpresa.length === 0 ? (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Sem breakdown por empresa
                </p>
              ) : (
                <div className="h-full min-h-[16rem] w-full min-w-0">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                    <BarChart
                      layout="vertical"
                      data={multasPorEmpresa}
                      margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={88}
                        tick={{ fontSize: 10 }}
                        interval={0}
                      />
                      <Tooltip content={SafeChartTooltip} />
                      <Legend />
                      <Bar dataKey="pagas" name="Pagas" fill={COLOR_C} stackId="m" isAnimationActive={false} />
                      <Bar dataKey="aVencer" name="A vencer" fill={COLOR_B} stackId="m" isAnimationActive={false} />
                      <Bar dataKey="vencidas" name="Vencidas" fill={COLOR_A} stackId="m" isAnimationActive={false} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartBlockBoundary>
            )}
          </CardContent>
        </Card>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {safeIndicators.map((ind) => {
          if (loadingById?.[ind.id]) {
            return (
              <DiretoriaIndicatorCardSkeleton
                key={ind.id}
                title={ind.title}
                description={ind.shortDescription}
              />
            );
          }
          const kpis = Array.isArray(ind.kpis) ? ind.kpis : [];
          const pk = Math.min(ind.primaryKpiIndex ?? 0, Math.max(0, kpis.length - 1));
          const primary = kpis[pk];
          const secondary = kpis.filter((_, i) => i !== pk);
          let sqlProps: DiretoriaSqlButtonProps = {};
          try {
            sqlProps = resolveIndicatorSql(ind) ?? {};
          } catch (err) {
            console.error('resolveIndicatorSql failed', ind.id, err);
          }
          return (
            <Card key={ind.id} className="relative h-full border-border/80 hover:shadow-card-hover">
              <div className="absolute right-2 top-2 z-10">
                <DiretoriaSqlButton {...sqlProps} stopPropagation />
              </div>
              <button
                type="button"
                onClick={() => onOpenIndicator(ind)}
                className="w-full text-left transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <CardHeader className="pb-2 pr-10">
                  <CardTitle className="text-base leading-snug">{ind.title}</CardTitle>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{ind.shortDescription}</p>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  {primary ? (
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {primary.label}
                      </p>
                      <p
                        className={cn(
                          'break-words font-heading text-xl font-bold tabular-nums sm:text-2xl',
                          diretoriaToneTextClass(primary.tone)
                        )}
                      >
                        {primary.value}
                      </p>
                    </div>
                  ) : null}
                  <ChartBlockBoundary fallback="Prévia indisponível">
                    <DiretoriaSparkline indicator={ind} />
                  </ChartBlockBoundary>
                  {secondary.map((k, i) => (
                    <div
                      key={`${k?.label ?? i}`}
                      className="flex items-baseline justify-between gap-2 border-t border-border/60 pt-2 first:border-t-0 first:pt-0"
                    >
                      <span className="min-w-0 shrink text-xs text-muted-foreground">
                        {k?.label ?? '—'}
                      </span>
                      <span
                        className={cn(
                          'max-w-[60%] break-words text-right text-sm font-semibold',
                          diretoriaToneTextClass(k?.tone)
                        )}
                      >
                        {k?.value ?? '—'}
                      </span>
                    </div>
                  ))}
                  <span className="inline-block pt-1 text-xs font-medium text-primary">Ver análise</span>
                </CardContent>
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
